import type { SttSegment, SpeechRecognizerConfig } from './SpeechRecognizerManager';

const SILENCE_TIMEOUT_MS = 4000;
const MIN_SESSION_DURATION_MS = 1000;

const ERROR_CODE_MAP: Record<string, number> = {
  'no-speech': 0,
  aborted: 1,
  'audio-capture': 2,
  network: 3,
  'not-allowed': 4,
  'service-not-allowed': 5,
  'bad-grammar': 6,
  'language-not-supported': 7,
};

export type SessionEndReason = 'natural' | 'rapid' | 'error' | 'fatal' | 'silence';

export interface SessionCallbacks {
  onPartialResult: (text: string) => void;
  onSegmentComplete: (segment: SttSegment) => void;
  onError: (error: { code: number; message: string }) => void;
  onSessionEnd: (reason: SessionEndReason) => void;
}

/**
 * 단일 SpeechRecognition 세션의 수명을 관리한다.
 * 자기 자신을 재시작하지 않으며, 세션 종료 시 Manager에게 사유를 보고만 한다.
 */
export class SpeechSession {
  private recognition: SpeechRecognition;

  private disposed = false;

  private startTime = Date.now();

  private lastRecognizedText = '';

  private recognitionStartTime: number | null = null;

  private lastRecognitionTime: number | null = null;

  private silenceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    Ctor: new () => SpeechRecognition,
    private config: SpeechRecognizerConfig,
    private callbacks: SessionCallbacks
  ) {
    const recognition = new Ctor();
    recognition.continuous = config.continuous;
    recognition.interimResults = true;
    recognition.lang = config.lang;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      this.startTime = Date.now();
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      this.handleResult(event);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      this.handleError(event);
    };

    recognition.onend = () => {
      this.handleEnd();
    };

    this.recognition = recognition;
  }

  start(): void {
    if (this.disposed) return;

    try {
      this.recognition.start();
    } catch (err) {
      this.disposed = true;
      this.callbacks.onError({
        code: -2,
        message: `Failed to start: ${err instanceof Error ? err.message : String(err)}`,
      });
      this.callbacks.onSessionEnd('error');
    }
  }

  /**
   * 세션을 즉시 종료. 이후 모든 콜백은 무시된다.
   * pending interim이 있으면 segment로 확정 후 종료.
   */
  dispose(): SttSegment | null {
    if (this.disposed) return null;
    this.disposed = true;
    this.clearSilenceTimer();

    let pendingSegment: SttSegment | null = null;
    if (this.lastRecognizedText) {
      pendingSegment = this.buildSegment(this.lastRecognizedText, null);
    }

    this.recognition.abort();
    return pendingSegment;
  }

  private handleResult(event: SpeechRecognitionEvent): void {
    if (this.disposed) return;

    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      if (!result) continue;

      const transcript = result[0]?.transcript ?? '';
      const confidence = result[0]?.confidence ?? null;
      const isFinal = result.isFinal;

      if (transcript.trim() === '' && this.lastRecognizedText !== '') continue;

      const now = Date.now();
      if (this.recognitionStartTime === null) {
        this.recognitionStartTime = now;
      }
      this.lastRecognitionTime = now;

      if (transcript !== this.lastRecognizedText) {
        this.lastRecognizedText = transcript;
        this.callbacks.onPartialResult(transcript);
      }

      if (isFinal) {
        this.callbacks.onSegmentComplete(this.buildSegment(transcript, confidence));
        this.clearSilenceTimer();
        this.lastRecognizedText = '';
        this.recognitionStartTime = null;
        this.lastRecognitionTime = null;
      } else {
        this.resetSilenceTimer();
      }
    }
  }

  private handleError(event: SpeechRecognitionErrorEvent): void {
    if (this.disposed) return;

    const { error } = event;

    if (error === 'aborted' || error === 'no-speech') {
      return;
    }

    const isFatal = error === 'not-allowed' || error === 'service-not-allowed';

    this.callbacks.onError({
      code: ERROR_CODE_MAP[error] ?? -1,
      message: error,
    });

    if (this.lastRecognizedText) {
      this.callbacks.onSegmentComplete(this.buildSegment(this.lastRecognizedText, null));
      this.lastRecognizedText = '';
    }

    this.disposed = true;
    this.clearSilenceTimer();
    this.recognition.abort();
    this.callbacks.onSessionEnd(isFatal ? 'fatal' : 'error');
  }

  private handleEnd(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearSilenceTimer();

    if (this.lastRecognizedText) {
      this.callbacks.onSegmentComplete(this.buildSegment(this.lastRecognizedText, null));
      this.lastRecognizedText = '';
    }

    const sessionDurationMs = Date.now() - this.startTime;
    const reason: SessionEndReason =
      sessionDurationMs < MIN_SESSION_DURATION_MS ? 'rapid' : 'natural';

    this.callbacks.onSessionEnd(reason);
  }

  private buildSegment(text: string, confidence: number | null): SttSegment {
    return {
      text: text.trim(),
      sttCreatedStartAt: this.recognitionStartTime ?? Date.now(),
      sttCreatedEndAt: this.lastRecognitionTime ?? Date.now(),
      sttConfidence: confidence ?? null,
    };
  }

  private resetSilenceTimer(): void {
    this.clearSilenceTimer();
    this.silenceTimer = setTimeout(() => {
      if (this.disposed) return;
      this.disposed = true;

      if (this.lastRecognizedText) {
        this.callbacks.onSegmentComplete(this.buildSegment(this.lastRecognizedText, null));
        this.lastRecognizedText = '';
      }

      this.recognition.abort();
      this.callbacks.onSessionEnd('silence');
    }, SILENCE_TIMEOUT_MS);
  }

  private clearSilenceTimer(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }
}
